import { ILoggedUser } from '@shared/interfaces/IUser';
import { getAuthBridgeUrl } from '../utils/authEngineUrl.utils';
import type { TwoFactorMethod } from '../services/betterAuthClient';

const FORBIDDEN_STATUS_CODE: number = 403;

/**
 * The bridge turned an engine session into the legacy auth payload. Byte-identical in shape to
 * what `POST /api/auth/authentication` returns, which is what lets mobile's existing post-login
 * path (`setLoggedUserThunk`) stay untouched.
 */
export interface IBridgeOkResult {
	outcome: 'ok';
	loggedUser: ILoggedUser;
}

/**
 * The bridge answered 403 `{ enrollmentRequired: true, methods: [...] }` — the caller's role is
 * in the server's `mfa.requiredRoles` and they have no second factor yet. Not an error: the
 * engine session is live and the password is already proven, so every engine call (including
 * `twoFactor.enable`) works. The user is sent to enrollment and the bridge is retried after.
 */
export interface IBridgeEnrollmentRequiredResult {
	outcome: 'enrollmentRequired';
	methods: TwoFactorMethod[];
}

export type BridgeResult = IBridgeOkResult | IBridgeEnrollmentRequiredResult;

interface IEnrollmentRequiredBody {
	enrollmentRequired?: boolean;
	methods?: TwoFactorMethod[];
}

const ENROLLMENT_FALLBACK_METHODS: TwoFactorMethod[] = ['totp'];

/** A bridge refusal carrying its HTTP status, so callers can tell "no session" (401) from a fault. */
export interface IBridgeError extends Error {
	status: number;
}

const bridgeError = (status: number): IBridgeError => {
	const error: IBridgeError = new Error(`Auth bridge rejected the engine session (status ${status})`) as IBridgeError;
	error.status = status;
	return error;
};

const readJsonBody = async (response: Response): Promise<unknown> => {
	try {
		return await response.json();
	} catch {
		return null;
	}
};

/**
 * Exchanges the Better Auth engine session for the legacy `ILoggedUser` payload.
 *
 * Deliberately **not** on `requestService`: the only credential this endpoint accepts is the
 * engine's httpOnly session cookie, and `requestService`'s axios instance neither sends cookies
 * (`withCredentials` is off) nor should start to — every other request in the app authenticates
 * with the bearer access token, and turning cookies on globally would attach them to all of
 * them. A bodyless `fetch` with `credentials: 'include'` keeps that blast radius at this one
 * call. It also keeps the bridge off the 401 interceptor, which reacts to a dead session by
 * logging out — the very thing a silent bootstrap attempt must be allowed to fail at quietly.
 *
 * Throws on anything that is not a 200 or the pinned 403 — a neutral 401 (no live session,
 * unknown or deactivated user) included. Callers must not translate the reason into UI text.
 */
export const mintTokensFromEngineSession = async (): Promise<BridgeResult> => {
	const response: Response = await fetch(getAuthBridgeUrl(), {
		method: 'POST',
		credentials: 'include',
		headers: {
			Accept: 'application/json',
		},
	});

	if (response.status === FORBIDDEN_STATUS_CODE) {
		const body: IEnrollmentRequiredBody = ((await readJsonBody(response)) ?? {}) as IEnrollmentRequiredBody;
		if (body.enrollmentRequired) {
			return {
				outcome: 'enrollmentRequired',
				methods: body.methods?.length ? body.methods : ENROLLMENT_FALLBACK_METHODS,
			};
		}
		throw bridgeError(response.status);
	}

	if (!response.ok) {
		throw bridgeError(response.status);
	}

	const loggedUser: ILoggedUser = (await readJsonBody(response)) as ILoggedUser;
	if (!loggedUser?.accessToken) {
		throw new Error('Auth bridge returned no user data');
	}
	return { outcome: 'ok', loggedUser };
};
